import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, ArrowRight, ArrowLeft, CheckCircle2, Calendar, Phone, Mail,
  Building2, Users, MessageSquare, Clock, AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || 'https://roniapi.data-corner.com.au';

const FIRM_SIZES = [
  { value: 'solo', label: 'Solo / 1-10 clients' },
  { value: 'small', label: '10-50 clients' },
  { value: 'medium', label: '50-200 clients' },
  { value: 'large', label: '200-1000 clients' },
  { value: 'enterprise', label: '1000+ clients' },
];

const TIME_PREFERENCES = [
  { value: 'this-week', label: 'This week', icon: Clock },
  { value: 'next-week', label: 'Next week', icon: Calendar },
  { value: 'flexible', label: 'Flexible', icon: ArrowRight },
];

const VALUE_BULLETS = [
  { title: 'Live walkthrough', body: '20 minutes of RoniDoc on your screen with one of your real anonymised documents.' },
  { title: 'Compliance demo', body: 'Watch the AI catch ATO and ASIC issues a junior accountant might miss.' },
  { title: 'Xero / QuickBooks live', body: 'See your figures pulled in real time and turned into a one-page advisory brief.' },
  { title: 'Pricing tailored to you', body: 'No surprise SaaS pricing - we share what makes sense for your firm size.' },
  { title: 'Q&A', body: 'Ask anything: data security, onboarding, integrations, partnership terms.' },
];

export default function BookDemoPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    firm: '',
    phone: '',
    size: '',
    time: 'this-week',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const canSubmit = form.name.trim() && form.email.trim() && form.firm.trim() && form.size && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/public/demo-request`, {
        name: form.name.trim(),
        email: form.email.trim(),
        firm: form.firm.trim(),
        phone: form.phone.trim() || undefined,
        size: form.size,
        time: form.time,
        notes: form.notes.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#262635] text-white relative overflow-x-hidden">
      {/* Ambient gradient blobs (matches landing) */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-1/4 left-1/4 w-[600px] h-[600px] bg-[#5048e5]/10 rounded-full blur-[180px]" />
        <div className="absolute bottom-1/4 right-1/5 w-[500px] h-[500px] bg-[#7c3aed]/12 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#7c75f0]" />
          <span className="text-base tracking-[0.15em] font-light">
            RONIDOC<span className="font-semibold">AI</span>
          </span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to home
        </Link>
      </header>

      <main className="relative z-10 px-6 md:px-12 lg:px-24 py-12">
        {submitted ? (
          <SuccessScreen email={form.email} name={form.name} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-20 max-w-6xl mx-auto">
            {/* LEFT - Form */}
            <div>
              <p className="text-[11px] md:text-[13px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-6">
                Free Demo · 30 minutes
              </p>
              <h1 className="text-[clamp(2rem,6vw,4.5rem)] font-extralight leading-[1.05] tracking-tight">
                See RoniDoc working on
                <span className="block font-bold bg-gradient-to-br from-white via-white to-[#bdb4ff] bg-clip-text text-transparent">
                  your firm's data.
                </span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
                Book a 30-minute call. We'll connect to a sandbox of one of your clients (or a sample dataset), show you compliance checks, advisory briefs, and exactly where AI saves you the most time.
              </p>

              <form onSubmit={handleSubmit} className="mt-10 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    icon={Users}
                    label="Your name"
                    required
                    value={form.name}
                    onChange={v => update('name', v)}
                    placeholder="Jane Accountant"
                  />
                  <Field
                    icon={Building2}
                    label="Firm name"
                    required
                    value={form.firm}
                    onChange={v => update('firm', v)}
                    placeholder="Beacon Accounting"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    icon={Mail}
                    label="Work email"
                    type="email"
                    required
                    value={form.email}
                    onChange={v => update('email', v)}
                    placeholder="jane@firm.com.au"
                  />
                  <Field
                    icon={Phone}
                    label="Phone (optional)"
                    type="tel"
                    value={form.phone}
                    onChange={v => update('phone', v)}
                    placeholder="04xx xxx xxx"
                  />
                </div>

                {/* Firm size - pill grid */}
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-3">
                    Firm size <span className="text-rose-400">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {FIRM_SIZES.map(opt => {
                      const active = form.size === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => update('size', opt.value)}
                          className={cn(
                            'px-3 py-3 text-xs tracking-wide border rounded-xl transition-all text-left',
                            active
                              ? 'border-[#7c75f0] bg-[#5048e5]/15 text-white'
                              : 'border-zinc-800 bg-zinc-900/30 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
                          )}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time preference */}
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-3">
                    When works best?
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {TIME_PREFERENCES.map(opt => {
                      const Icon = opt.icon;
                      const active = form.time === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => update('time', opt.value)}
                          className={cn(
                            'flex flex-col items-center gap-1.5 px-3 py-3 text-xs tracking-wide border rounded-xl transition-all',
                            active
                              ? 'border-[#7c75f0] bg-[#5048e5]/15 text-white'
                              : 'border-zinc-800 bg-zinc-900/30 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">
                    Anything specific you want us to focus on?
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3.5 h-4 w-4 text-zinc-600" />
                    <textarea
                      value={form.notes}
                      onChange={e => update('notes', e.target.value)}
                      placeholder="e.g. We deal with a lot of trusts and SMSFs and want to see how RoniDoc handles them."
                      rows={3}
                      className="w-full pl-10 pr-4 py-3 bg-zinc-900/40 border border-zinc-800 rounded-xl text-sm text-white placeholder:text-zinc-600 focus:border-[#7c75f0] focus:outline-none resize-none"
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 border border-rose-500/30 bg-rose-500/10 rounded-xl p-3 text-sm text-rose-200">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={cn(
                    'group inline-flex items-center justify-center gap-2 w-full md:w-auto bg-[#5048e5] hover:bg-[#5f58e8] disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-[12px] tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-all duration-300',
                  )}
                >
                  {submitting ? (
                    <>
                      <span className="h-3 w-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Book my demo
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition" />
                    </>
                  )}
                </button>

                <p className="text-[11px] text-zinc-600">
                  We'll reply within one business day. By submitting you agree to our{' '}
                  <Link to="/privacy-policy" className="underline hover:text-zinc-400">privacy policy</Link>.
                </p>
              </form>
            </div>

            {/* RIGHT - What you'll see */}
            <aside className="lg:sticky lg:top-12 lg:self-start">
              <div className="border border-zinc-800 bg-zinc-900/30 backdrop-blur-md rounded-2xl p-8">
                <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-4">
                  In the demo
                </p>
                <h2 className="text-2xl md:text-3xl font-light leading-tight mb-8">
                  What 30 minutes with RoniDoc looks like
                </h2>
                <ul className="space-y-5">
                  {VALUE_BULLETS.map((b, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#5048e5]/15 border border-[#7c75f0]/40 flex items-center justify-center mt-0.5">
                        <CheckCircle2 className="h-3 w-3 text-[#7c75f0]" />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-white">{b.title}</p>
                        <p className="text-xs text-zinc-400 leading-relaxed mt-0.5">{b.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 border border-zinc-800/60 bg-zinc-900/20 rounded-2xl p-5 text-center">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Already a customer? <Link to="/login" className="text-[#7c75f0] hover:text-white">Sign in</Link>
                </p>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({
  icon: Icon, label, required, value, onChange, placeholder, type = 'text',
}: {
  icon: any;
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">
        {label} {required && <span className="text-rose-400">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="w-full pl-10 pr-4 py-3 bg-zinc-900/40 border border-zinc-800 rounded-xl text-sm text-white placeholder:text-zinc-600 focus:border-[#7c75f0] focus:outline-none"
        />
      </div>
    </div>
  );
}

function SuccessScreen({ name, email }: { name: string; email: string }) {
  return (
    <div className="max-w-2xl mx-auto py-20 text-center">
      <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-emerald-500/15 border border-emerald-400/40 mb-8">
        <CheckCircle2 className="h-10 w-10 text-emerald-400" />
      </div>
      <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-4">Demo Requested</p>
      <h1 className="text-[clamp(2rem,5vw,3.5rem)] font-extralight leading-[1.1] tracking-tight">
        Thanks {name?.split(' ')[0] || 'there'} - we're on it.
      </h1>
      <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">
        We'll send a calendar invite to <span className="text-white font-medium">{email}</span> within one business day.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#c4b5fd] mb-2">Step 1</p>
          <p className="text-sm font-medium text-white">We confirm a time</p>
          <p className="text-xs text-zinc-500 mt-1">Email + calendar invite within 24h.</p>
        </div>
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#c4b5fd] mb-2">Step 2</p>
          <p className="text-sm font-medium text-white">We prep with your data</p>
          <p className="text-xs text-zinc-500 mt-1">Sandbox setup, no production access required.</p>
        </div>
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#c4b5fd] mb-2">Step 3</p>
          <p className="text-sm font-medium text-white">30-minute live walkthrough</p>
          <p className="text-xs text-zinc-500 mt-1">No deck. Real product. Your questions.</p>
        </div>
      </div>

      <div className="mt-12">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to home
        </Link>
      </div>
    </div>
  );
}
